import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import Typography from '@material-ui/core/Typography';

import { Field, Fields, reduxForm } from 'redux-form';

import RadioButtonField from 'components/fields/RadioButtonField';
import RadioGroupField from 'components/fields/RadioGroupField';
import DatepickerField from 'components/fields/DatepickerField';
import AutosuggestField from 'components/fields/AutosuggestField';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';

import dateFormat from 'config/dateFormat';
import orderStates from 'config/orderStates';

import pxToRem from 'styles/pxToRem';

import StatsLineChartFormStatusFields from './StatsLineChartFormStatusFields';

const styles = theme => ({
  root: {
    marginBottom: 16,
    padding: '8px 16px',
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
  },
  gridStateStatus: {
    position: 'relative',
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: '50%',
      width: 1,
      backgroundColor: theme.palette.grey[600],
      zIndex: 2,
    },
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: '50%',
      width: 6,
      marginLeft: -3,
      backgroundColor: theme.palette.common.white,
      borderTop: `1px solid ${theme.palette.grey[600]}`,
      borderBottom: `1px solid ${theme.palette.grey[600]}`,
      zIndex: 1,
    },
  },
  stateField: {
    display: 'flex',
  },
  stateFieldInputRoot: {
    display: 'flex',
  },
  typographyHeaderTitle: {
    fontSize: pxToRem(18),
    marginTop: 2,
    marginRight: 16,
  },
  gridItemDatepicker: {
    width: 225,
    flexBasis: 225,
  },
});

function onChange(values, dispatch, props, previousValues) {
  if (values['Date From']
    && values['Date To']
    && !validate.date(values['Date From'])
    && !validate.date(values['Date To'])) {
    if (!previousValues['Date Range'] && values['Date Range']) {
      props.clearFields(false, false, 'Date From');
      props.clearFields(false, false, 'Date To');
    } else {
      props.clearFields(false, false, 'Date Range');
    }
  }
  if (values.State !== values['Selected State'] && values['Workflow State ID']) {
    props.clearFields(false, false, 'Selected State');
    props.clearFields(false, false, 'Workflow State ID');
  }
  if (values.Status !== values['Selected Status'] && values['Workflow Status ID']) {
    props.clearFields(false, false, 'Selected Status');
    props.clearFields(false, false, 'Workflow Status ID');
  }
  if (previousValues['Workflow State ID'] !== values['Workflow State ID']) {
    props.clearFields(false, false, 'Status');
    props.clearFields(false, false, 'Selected Status');
    props.clearFields(false, false, 'Workflow Status ID');
  }
}

const validateForm = (values) => {
  const errors = {};

  if (values.State && values.State !== values['Selected State']) {
    errors.State = 'Invalid State';
  }

  if (values.Status && values.Status !== values['Selected Status']) {
    errors.Status = 'Invalid Status';
  }

  if (!values['Date Range'] && !values['Date From']) {
    errors['Date From'] = 'Field is required';
  }

  if (!values['Date Range'] && !values['Date To']) {
    errors['Date To'] = 'Field is required';
  }

  if (values['Date From'] && !errors['Date From']) {
    errors['Date From'] = validate.date(values['Date From']);
  }

  if (values['Date To'] && !errors['Date To']) {
    errors['Date To'] = validate.date(values['Date To']);
  }

  if (values['Date From']
    && !validate.date(values['Date From'])
    && values['Date To']
    && !validate.date(values['Date To'])
    && moment(values['Date To'], dateFormat).isBefore(moment(values['Date From'], dateFormat))) {
    errors['Date To'] = 'Date To should be after Date From';
  }

  return errors;
};

const StatsLineChartForm = ({
  handleSubmit,
  dateRangeMin,
  dateRangeMax,
  change,
  classes,
}) => (
  <form
    className={classes.root}
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
  >
    <Grid
      container
      justify="flex-start"
      spacing={16}
    >
      <Grid item>
        <Typography className={classes.typographyHeaderTitle}>
          Orders analytics
        </Typography>
      </Grid>
    </Grid>
    <Grid
      container
      justify="flex-start"
      spacing={16}
    >
      <Grid item>
        <FormControl role="group">
          <Field
            name="Date Range"
            component={RadioGroupField}
          >
            <RadioButtonField
              value="1w"
              label="Past Week"
              size="sm"
              color="primary"
            />
            <RadioButtonField
              value="1m"
              label="Past Month"
              size="sm"
              color="primary"
            />
            <RadioButtonField
              value="1y"
              label="Past Year"
              size="sm"
              color="primary"
            />
          </Field>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={5} className={classes.gridItemDatepicker}>
        <Fields
          names={[
            'Date From',
            'Date To',
          ]}
          normalize={{
            'Date From': normalize.date,
            'Date To': normalize.date,
          }}
          startDateName="Date From"
          endDateName="Date To"
          component={DatepickerField}
          fullWidth
          minDate={dateRangeMin || '01/01/1900'}
          maxDate={dateRangeMax || moment().format(dateFormat)}
          dateFormat={dateFormat}
          placeholder="mm/dd/yyyy"
          maxLength="10"
          variant="range"
          size="sm"
          color="light"
          rangeButtons
        />
      </Grid>
      <Grid item xs={12} sm={6} md={8}>
        <Grid
          className={classes.gridStateStatus}
          container
          justify="flex-start"
          wrap="nowrap"
          spacing={0}
        >
          <Grid
            item
            xs={6}
          >
            <Field
              className={classes.stateField}
              inputRootClassName={classes.stateFieldInputRoot}
              id="State"
              name="State"
              component={AutosuggestField}
              placeholder="-State-"
              fullWidth
              listWidth="auto"
              size="sm"
              color="light"
              renderSuggestionContent={suggestion => suggestion.Name}
              onSuggestionSelected={(suggestion) => {
                change('Workflow State ID', suggestion.ID);
                change('Selected State', suggestion.Name);
                return suggestion.Name;
              }}
              fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                if (offset === 0) {
                  if (value.length > 0) {
                    resolve(orderStates
                      .filter(item => item.Name.toLowerCase().indexOf(value.toLowerCase()) !== -1));
                  } else {
                    resolve(orderStates);
                  }
                } else {
                  resolve([]);
                }
              }))}
              onlyConfirmedSelection
            />
          </Grid>
          <Grid
            item
            xs={6}
          >
            <Fields
              id="State / Status"
              names={[
                'Workflow State ID',
                'Status',
                'Selected Status',
                'Workflow Status ID',
              ]}
              component={StatsLineChartFormStatusFields}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  </form>
);

StatsLineChartForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  dateRangeMin: PropTypes.string,
  dateRangeMax: PropTypes.string,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    stateField: PropTypes.string.isRequired,
    stateFieldInputRoot: PropTypes.string.isRequired,
  }).isRequired,
};

StatsLineChartForm.defaultProps = {
  dateRangeMin: null,
  dateRangeMax: null,
};

export default reduxForm({
  onChange,
  validate: validateForm,
})(withStyles(styles)(StatsLineChartForm));



// WEBPACK FOOTER //
// ./src/components/home/StatsLineChart/StatsLineChartForm.js